import React from 'react';
import { Badge, Popconfirm, Tooltip } from 'antd';
import {
  LoadingOutlined,
  CheckCircleFilled,
  EditOutlined,
  ExclamationCircleFilled,
  CloseOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import { useReplyComposer } from '../../../contexts/ReplyComposerContext';

const T = {
  brand: '#FF4500',
  accent: '#0079D3',
  bg: '#FFFFFF',
  bgMuted: '#F8F9FA',
  border: '#E5E7EB',
  text: '#0F0F10',
  textSecondary: '#5C5C66',
  textMuted: '#898A8F',
  success: '#047857',
  error: '#DC2626',
};

const STATUS_ICON: Record<string, React.ReactNode> = {
  generating: <LoadingOutlined style={{ fontSize: 14, color: T.brand }} spin />,
  ready: <CheckCircleFilled style={{ fontSize: 14, color: T.success }} />,
  composing: <EditOutlined style={{ fontSize: 14, color: T.accent }} />,
  error: <ExclamationCircleFilled style={{ fontSize: 14, color: T.error }} />,
};

const ReplyMinimizedRail: React.FC = () => {
  const { sessions, activeId, restoreSession, closeSession } = useReplyComposer();

  // Only show sessions that are NOT currently open in the drawer
  const minimized = sessions.filter((s) => s.id !== activeId);

  if (minimized.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        right: 0,
        top: 96,
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        paddingRight: 0,
        pointerEvents: 'auto',
      }}
    >
      {minimized.map((session) => {
        const postTitle = session.post.post_title || '';
        const subreddit = session.post.subreddit || '';
        const displayLabel =
          postTitle.length > 0
            ? postTitle.slice(0, 28) + (postTitle.length > 28 ? '…' : '')
            : `r/${subreddit}`;

        const canClose = session.status !== 'generating';
        const showConfirm =
          session.status === 'ready' &&
          session.savedReplyTexts.length > 0;

        return (
          <Tooltip
            key={session.id}
            title={
              <div>
                <div style={{ fontWeight: 600, marginBottom: 2 }}>
                  {postTitle || `r/${subreddit}`}
                </div>
                <div style={{ fontSize: 11, opacity: 0.8 }}>
                  {session.status === 'generating'
                    ? 'Generating reply…'
                    : session.status === 'ready'
                    ? 'Reply ready — click to view'
                    : session.status === 'error'
                    ? 'Generation failed'
                    : 'Click to open'}
                </div>
              </div>
            }
            placement="left"
            mouseEnterDelay={0.3}
          >
            <div
              style={{
                position: 'relative',
                width: 44,
                minHeight: 120,
                backgroundColor: T.bg,
                border: `1px solid ${T.border}`,
                borderRight: 'none',
                borderRadius: '8px 0 0 8px',
                boxShadow: '-2px 2px 8px rgba(0,0,0,0.08)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                paddingTop: 10,
                paddingBottom: 10,
                cursor: 'pointer',
                transition: 'box-shadow 0.15s ease',
                overflow: 'hidden',
              }}
              onClick={() => restoreSession(session.id)}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.boxShadow =
                  '-3px 2px 12px rgba(0,0,0,0.14)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.boxShadow =
                  '-2px 2px 8px rgba(0,0,0,0.08)';
              }}
            >
              {/* Robot icon at top */}
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 4,
                  background: T.brand,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  marginBottom: 6,
                }}
              >
                <RobotOutlined style={{ color: '#fff', fontSize: 12 }} />
              </div>

              {/* Status icon with unseen-result badge */}
              <Badge
                dot
                offset={[2, -2]}
                style={{
                  backgroundColor: T.brand,
                  display: session.hasUnseenResult ? undefined : 'none',
                }}
              >
                <div style={{ marginBottom: 8 }}>
                  {STATUS_ICON[session.status] ?? STATUS_ICON.composing}
                </div>
              </Badge>

              {/* Vertical post label */}
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  width: '100%',
                  padding: '0 4px',
                }}
              >
                <span
                  style={{
                    writingMode: 'vertical-rl',
                    textOrientation: 'mixed',
                    transform: 'rotate(180deg)',
                    fontSize: 11,
                    fontWeight: 500,
                    color: T.textSecondary,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    maxHeight: 100,
                    textOverflow: 'ellipsis',
                    lineHeight: 1.3,
                    userSelect: 'none',
                  }}
                >
                  {displayLabel}
                </span>
              </div>

              {/* Close button — hidden while generating */}
              {canClose && (
                <div
                  style={{ marginTop: 6, flexShrink: 0 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {showConfirm ? (
                    <Popconfirm
                      title="Discard this reply session?"
                      okText="Discard"
                      cancelText="Keep"
                      okButtonProps={{ danger: true, size: 'small' }}
                      cancelButtonProps={{ size: 'small' }}
                      onConfirm={() => closeSession(session.id)}
                      placement="left"
                    >
                      <div
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: 4,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: T.textMuted,
                          cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLDivElement).style.color = T.error;
                          (e.currentTarget as HTMLDivElement).style.backgroundColor = '#FEF2F2';
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLDivElement).style.color = T.textMuted;
                          (e.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent';
                        }}
                      >
                        <CloseOutlined style={{ fontSize: 10 }} />
                      </div>
                    </Popconfirm>
                  ) : (
                    <div
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: T.textMuted,
                        cursor: 'pointer',
                      }}
                      onClick={() => closeSession(session.id)}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLDivElement).style.color = T.error;
                        (e.currentTarget as HTMLDivElement).style.backgroundColor = '#FEF2F2';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLDivElement).style.color = T.textMuted;
                        (e.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent';
                      }}
                    >
                      <CloseOutlined style={{ fontSize: 10 }} />
                    </div>
                  )}
                </div>
              )}
            </div>
          </Tooltip>
        );
      })}
    </div>
  );
};

export default ReplyMinimizedRail;
